import * as React from 'react';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { Surface } from './Card';
import { Switch } from './Checkbox';
import { Icon, type IconComponent } from './Icon';
import { Stack } from './Layout';
import { ListRow } from './List';
import { Text } from './Text';

export interface SettingsListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The group's name, above its card: "Notifications", "Account". */
  title?: React.ReactNode;
  /** A line under the card: what the group's settings affect. */
  footnote?: React.ReactNode;
  /** SettingsRows. */
  children: React.ReactNode;
}

/**
 * A group of settings: its name, then its rows in one card with rules
 * between them, as a phone's settings screen draws them.
 *
 *   <SettingsList title="Notifications">
 *     <SettingsRow label="Push notifications" toggle={{ checked, onCheckedChange }} />
 *     <SettingsRow label="Send a test" onClick={sendTest} />
 *   </SettingsList>
 */
export const SettingsList = React.forwardRef<HTMLDivElement, SettingsListProps>(
  function SettingsList({ title, footnote, children, ...props }, ref) {
    return (
      <Stack as="section" ref={ref} gap={2} {...props}>
        {title ? (
          <Text variant="overline" tone="muted" as="h2">
            {title}
          </Text>
        ) : null}
        <Surface bordered radius="lg">
          <Stack gap={0} divided>
            {children}
          </Stack>
        </Surface>
        {footnote ? (
          <Text variant="caption" tone="muted">
            {footnote}
          </Text>
        ) : null}
      </Stack>
    );
  }
);

export interface SettingsRowProps
  extends Omit<
    React.HTMLAttributes<HTMLElement>,
    'onClick' | 'onSelect' | 'children' | 'title'
  > {
  label: React.ReactNode;
  /** A line under the label. */
  description?: React.ReactNode;
  /** A lucide icon, or a Phosphor one as the mobile app's Figma draws. */
  icon?: IconComponent;
  /** The current value, on the right: "Essential", "On duty", "2.4.1". */
  value?: React.ReactNode;
  /** The row opens something: it becomes a button with a chevron. */
  onClick?: () => void;
  /**
   * The row opens a page outside the app, the dashboard in the phone's
   * browser: an arrow out instead of the chevron, so leaving is not a
   * surprise.
   */
  external?: boolean;
  /** The row is an on/off setting: a switch on the right. */
  toggle?: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
  };
  /** Anything else on the right: a button, a badge. */
  control?: React.ReactNode;
  /** Sign out, delete account: the label in the destructive colour. */
  destructive?: boolean;
  disabled?: boolean;
}

/**
 * One setting: a label, maybe a line under it, and on the right its value
 * and a chevron when it opens something, or a switch when it is on or off.
 *
 *   <SettingsRow label="Plan" value="Essential" onClick={openPlan} />
 *   <SettingsRow label="Push notifications"
 *     toggle={{ checked: on, onCheckedChange: setOn }} />
 *   <SettingsRow label="Delete account" destructive onClick={confirmDelete} />
 *
 * A row that opens something is one button, so the whole width is the
 * target. A switch row is not a button; the switch is, named by the label.
 *
 * A row can open a sheet as its trigger, the value showing what is chosen,
 * rather than holding a field it has no room for:
 *
 *   <OptionSheet title="Company" options={companies} value={id}
 *     onValueChange={switchCompany}
 *     trigger={<SettingsRow label="Company" value={companyName} />} />
 */
export const SettingsRow = React.forwardRef<HTMLElement, SettingsRowProps>(
  function SettingsRow(
    {
      label,
      description,
      icon,
      value,
      onClick,
      external = false,
      toggle,
      control,
      destructive = false,
      disabled = false,
      ...props
    },
    ref
  ) {
    const labelId = React.useId();
    const text = (
      <Stack gap={0} grow>
        <Text
          as="span"
          id={labelId}
          variant="body"
          tone={destructive ? 'destructive' : 'default'}
        >
          {label}
        </Text>
        {description ? (
          <Text as="span" variant="caption" tone="muted">
            {description}
          </Text>
        ) : null}
      </Stack>
    );
    const leading = icon ? (
      <Icon
        icon={icon}
        size="md"
        tone={destructive ? 'destructive' : 'muted'}
      />
    ) : null;
    const shown = value ? (
      <Text as="span" variant="body" tone="muted" className="shrink-0">
        {value}
      </Text>
    ) : null;

    if (onClick) {
      return (
        <ListRow
          {...props}
          ref={ref}
          as="button"
          interactive
          inset="md"
          onClick={onClick}
          disabled={disabled}
        >
          {leading}
          {text}
          {shown}
          {control}
          {destructive ? null : (
            <Icon
              icon={external ? ArrowUpRight : ChevronRight}
              size="sm"
              tone="muted"
            />
          )}
        </ListRow>
      );
    }

    return (
      <ListRow {...props} ref={ref} inset="md" disabled={disabled}>
        {leading}
        {text}
        {shown}
        {control}
        {toggle ? (
          <Switch
            checked={toggle.checked}
            onCheckedChange={toggle.onCheckedChange}
            disabled={disabled || toggle.disabled}
            aria-labelledby={labelId}
          />
        ) : null}
      </ListRow>
    );
  }
);
